<div class="data-table tax-payments" data-cy="tax-payments-section">
  <div class="data-table__head tax-payments__head">
    <div class="grid-row grid-row--aic tax-payments__head-row">
      <div class="grid-col tax-payments__head-title-col">
        <h3 class="tax-payments__title">Tax Payments</h3>
      </div>
      <!--
        Status filter dropdown (AIW-799). Renders only when there's data to
        filter; hidden in loading / error / empty states so the section header
        stays clean. The metric tiles act as the status filter — pilot
        feedback was that the standalone `<select>` next to them was
        redundant noise.
      -->
    </div>
  </div>

  <!--
    Connect Remitian banner: shown when at least one row is in
    AWAITING_TAXPAYER_SETUP. The return trip from Remitian lands back here
    with ?remitian-connected=1, which force-refreshes the query and clears
    this banner once the backend flips the rows to UPCOMING.

    Go to Remitian banner: shown when the taxpayer is connected (no
    AWAITING_TAXPAYER_SETUP rows) but has tax payments. Takes them straight
    into Remitian with no redirect back, so they can manage or pay at their
    own pace. The two banners are mutually exclusive.
  -->
  <ng-container *ngIf="showConnectRemitianBanner$ | async; else goToRemitianBanner">
    <div
      class="tax-payments__connect-banner"
      data-cy="tax-payments-connect-remitian-banner"
    >
      <div class="tax-payments__connect-banner-text">
        <h4 class="tax-payments__connect-banner-title">Connect Remitian to enable payments</h4>
        <p class="tax-payments__connect-banner-body">
          Your firm scheduled a tax payment for you. Finish setting up your Remitian
          account to enable it.
        </p>
      </div>
      <button
        type="button"
        class="button button--primary tax-payments__connect-banner-btn"
        [disabled]="connectRemitianLoading"
        (click)="onConnectRemitianClick()"
        data-cy="tax-payments-connect-remitian-btn"
      >
        <span *ngIf="!connectRemitianLoading">Connect Remitian</span>
        <span *ngIf="connectRemitianLoading">Opening&hellip;</span>
      </button>
    </div>
  </ng-container>

  <ng-template #goToRemitianBanner>
    <div
      *ngIf="showGoToRemitianBanner$ | async"
      class="tax-payments__connect-banner"
      data-cy="tax-payments-go-to-remitian-banner"
    >
      <div class="tax-payments__connect-banner-text">
        <h4 class="tax-payments__connect-banner-title">Manage your tax payments in Remitian</h4>
        <p class="tax-payments__connect-banner-body">
          Your Remitian account is connected. Go to Remitian to view and pay your
          scheduled tax payments.
        </p>
      </div>
      <button
        type="button"
        class="button button--primary tax-payments__connect-banner-btn"
        [disabled]="goToRemitianLoading"
        (click)="onGoToRemitianClick()"
        data-cy="tax-payments-go-to-remitian-btn"
      >
        <span *ngIf="!goToRemitianLoading">Go to Remitian</span>
        <span *ngIf="goToRemitianLoading">Opening&hellip;</span>
      </button>
    </div>
  </ng-template>

  <ng-container *ngIf="status$ | async as status">
    <!-- Loading skeleton -->
    <ng-container *ngIf="status === LoadableStatus.LOADING || status === LoadableStatus.DEFAULT">
      <div class="tax-payments__loading" data-cy="tax-payments-loading">
        <div class="tax-payments__skeleton"></div>
        <div class="tax-payments__skeleton"></div>
        <div class="tax-payments__skeleton"></div>
      </div>
    </ng-container>

    <!-- Error state -->
    <ng-container *ngIf="status === LoadableStatus.ERROR">
      <div class="tax-payments__error" data-cy="tax-payments-error">
        We couldn&apos;t load your tax payments. Please refresh and try again.
      </div>
    </ng-container>

    <!-- Loaded state -->
    <ng-container *ngIf="status === LoadableStatus.LOADED">
      <ng-container *ngIf="taxPayments$ | async as taxPayments">
        <!--
          Co-branded gradient empty state (AIW-802). Reached when the backend
          returns an empty list (e.g. tenant is gated on but has no scheduled
          tax payments yet, or Remitian sync hasn't populated). Goal is a
          trust signal for the logged-in client: their firm has set this up,
          payments are coming. Not a sales pitch (no "Connect Remitian" CTA).
        -->
        <div *ngIf="taxPayments.length === 0" class="tax-payments__empty" data-cy="tax-payments-empty">
          <div class="tax-payments__empty-gradient" aria-hidden="true"></div>
          <div class="tax-payments__empty-logos" aria-hidden="true">
            <img
              src="assets/AiwynLogo.png"
              alt=""
              class="tax-payments__empty-logo tax-payments__empty-logo--aiwyn"
            />
            <span class="tax-payments__empty-logo-divider"></span>
            <img
              src="assets/remitian-logo.svg"
              alt=""
              class="tax-payments__empty-logo tax-payments__empty-logo--remitian"
            />
          </div>
          <h3 class="tax-payments__empty-title">
            Your firm is set up with Remitian to process tax payments.
          </h3>
          <p class="tax-payments__empty-body">
            When your firm schedules a tax payment, it&apos;ll appear here. You&apos;ll
            get an email when there&apos;s an action to take.
          </p>
          <p *ngIf="firmContact.email" class="tax-payments__empty-help">
            Questions about your tax payments?
            <a
              [href]="'mailto:' + firmContact.email"
              data-cy="tax-payments-empty-contact-firm"
            >Contact your firm</a>.
          </p>
        </div>

        <!--
          Metric cards row (AIW-800). Renders when there's data to summarize.
          Counts compute from the unfiltered set so the 4 cards stay stable
          when a filter is active. Click toggles the filter to that status
          (or back to All if already selected).
        -->
        <div
          *ngIf="taxPayments.length > 0"
          class="tax-payments__metrics"
          data-cy="tax-payments-metrics"
        >
          <button
            *ngFor="let card of metricCards; trackBy: trackByMetricStatus"
            type="button"
            class="tax-payments__metric-card"
            [class.tax-payments__metric-card--active]="(selectedStatus$ | async) === card.status"
            [ngClass]="'tax-payments__metric-card--' + card.badgeVariant"
            (click)="onMetricCardClick(card.status)"
            [attr.aria-pressed]="(selectedStatus$ | async) === card.status"
            [attr.data-cy]="'tax-payments-metric-' + card.status.toLowerCase()"
          >
            <span class="tax-payments__metric-card-label">
              <span class="tax-payments__metric-card-dot" aria-hidden="true"></span>
              {{ card.label }}
            </span>
            <span class="tax-payments__metric-card-count">
              {{ ((metricCounts$ | async) ?? {})[card.status] ?? 0 }}
            </span>
          </button>
        </div>

        <!-- Mobile: stacked cards (filter applies; sort + paginate do not) -->
        <div
          *ngIf="taxPayments.length > 0 && configService.isMobile"
          class="tax-payments__cards"
          data-cy="tax-payments-mobile"
        >
          <ng-container *ngIf="filteredTaxPayments$ | async as filteredRows">
            <div
              *ngFor="let row of filteredRows; trackBy: trackByPmsRef"
              class="tax-payments__card"
              [class.tax-payments__card--awaiting-setup]="row.displayStatus === 'AWAITING_TAXPAYER_SETUP'"
              [attr.data-cy]="row.displayStatus === 'AWAITING_TAXPAYER_SETUP' ? 'tax-payment-row-awaiting-setup' : null"
            >
              <div class="tax-payments__card-row">
                <span class="tax-payments__card-value">{{ row.description }}</span>
                <div [ngClass]="'badge badge--' + row.badgeVariant">
                  <div class="badge__label">{{ row.statusLabel }}</div>
                </div>
              </div>
              <div class="tax-payments__card-row">
                <span class="tax-payments__card-label">Account</span>
                <span class="tax-payments__card-value">{{ row.taxpayerName }}</span>
              </div>
              <div class="tax-payments__card-row">
                <span class="tax-payments__card-label">Tax Authority</span>
                <span class="tax-payments__card-value">{{ row.taxAuthorityLabel }}</span>
              </div>
              <div class="tax-payments__card-row">
                <span class="tax-payments__card-label">Due Date</span>
                <span class="tax-payments__card-value">{{ row.paymentDate | date: 'MM/dd/yy' }}</span>
              </div>
              <div class="tax-payments__card-row">
                <span class="tax-payments__card-label">Amount</span>
                <span class="tax-payments__card-value">
                  {{ row.amount | currency : row.currency }}
                </span>
              </div>
              <div class="tax-payments__card-row" *ngIf="row.canPayNow">
                <span class="tax-payments__card-label"></span>
                <button
                  type="button"
                  class="button button--primary tax-payments__pay-now-btn"
                  [disabled]="isPayNowLoading(row)"
                  (click)="onPayNowClick(row)"
                  [attr.data-cy]="'tax-payments-pay-now-' + row.pmsRef"
                >
                  <span *ngIf="!isPayNowLoading(row)">Pay Now</span>
                  <span *ngIf="isPayNowLoading(row)">Opening…</span>
                </button>
              </div>
            </div>
            <!-- Empty filter result on mobile (still has unfiltered data) -->
            <div *ngIf="filteredRows.length === 0" class="tax-payments__filtered-empty">
              No tax payments match the selected status.
            </div>
          </ng-container>
        </div>

        <!-- Desktop: mat-table with sortable headers + paginator -->
        <div
          *ngIf="taxPayments.length > 0 && !configService.isMobile"
          class="data-table__cont"
          data-cy="tax-payments-desktop"
        >
          <table
            mat-table
            [dataSource]="dataSource"
            [trackBy]="trackByPmsRef"
            matSort
            matSortActive="paymentDate"
            matSortDirection="asc"
            (matSortChange)="onSortChange($event.active, $event.direction)"
            class="data-table__tbl"
          >
            <!-- Account (client name); matches the Payments/Invoices tables -->
            <ng-container matColumnDef="account">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Account</th>
              <td mat-cell *matCellDef="let row" data-cy="tax-payment-account">
                {{ row.taxpayerName }}
              </td>
            </ng-container>

            <!-- Description (unsortable; descriptions are noisy) -->
            <ng-container matColumnDef="description">
              <th mat-header-cell *matHeaderCellDef>Description</th>
              <td mat-cell *matCellDef="let row" data-cy="tax-payment-description">
                {{ row.description }}
              </td>
            </ng-container>

            <!-- Tax Authority (unsortable for V1.5) -->
            <ng-container matColumnDef="taxAuthority">
              <th mat-header-cell *matHeaderCellDef>Tax Authority</th>
              <td mat-cell *matCellDef="let row" data-cy="tax-payment-authority">
                {{ row.taxAuthorityLabel }}
                <span *ngIf="row.taxYear" class="tc--dark"> &middot; {{ row.taxYear }}</span>
              </td>
            </ng-container>

            <!-- Due Date (sortable, default ASC: surfaces overdue at top) -->
            <ng-container matColumnDef="paymentDate">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Due Date</th>
              <td mat-cell *matCellDef="let row" data-cy="tax-payment-date">
                {{ row.paymentDate | date: 'MM/dd/yy' }}
              </td>
            </ng-container>

            <!-- Amount (sortable, numeric) -->
            <ng-container matColumnDef="amount">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Amount</th>
              <td mat-cell *matCellDef="let row" data-cy="tax-payment-amount">
                {{ row.amount | currency : row.currency }}
              </td>
            </ng-container>

            <!-- Status (sortable; sort uses status priority via the data accessor) -->
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
              <td mat-cell *matCellDef="let row" data-cy="tax-payment-status">
                <div [ngClass]="'badge badge--' + row.badgeVariant">
                  <div class="badge__label">{{ row.statusLabel }}</div>
                </div>
              </td>
            </ng-container>

            <!-- Pay Now (payable rows) / paid indicator (Processed rows) -->
            <ng-container matColumnDef="payNow">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let row" class="tax-payments__pay-now-cell">
                <button
                  *ngIf="row.canPayNow"
                  type="button"
                  class="button button--primary tax-payments__pay-now-btn"
                  [disabled]="isPayNowLoading(row)"
                  (click)="onPayNowClick(row)"
                  [attr.data-cy]="'tax-payments-pay-now-' + row.pmsRef"
                >
                  <span *ngIf="!isPayNowLoading(row)">Pay Now</span>
                  <span *ngIf="isPayNowLoading(row)">Opening…</span>
                </button>
                <span
                  *ngIf="!row.canPayNow && row.displayStatus !== 'AWAITING_TAXPAYER_SETUP'"
                  class="tax-payments__paid-indicator"
                  aria-hidden="true"
                  data-cy="tax-payments-paid-indicator"
                >&mdash;</span>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="columnsToDisplay"></tr>
            <tr
              mat-row
              *matRowDef="let row; columns: columnsToDisplay"
              [class.tax-payments__row--awaiting-setup]="row.displayStatus === 'AWAITING_TAXPAYER_SETUP'"
              [attr.data-cy]="row.displayStatus === 'AWAITING_TAXPAYER_SETUP' ? 'tax-payment-row-awaiting-setup' : 'tax-payment-row'"
            ></tr>

            <!-- No-rows row when the filter empties the table -->
            <tr class="mat-row tax-payments__filtered-empty-row" *matNoDataRow>
              <td class="mat-cell" [attr.colspan]="columnsToDisplay.length">
                No tax payments match the selected status.
              </td>
            </tr>
          </table>

          <mat-paginator
            *ngIf="dataSource"
            [pageSize]="5"
            [pageSizeOptions]="[5, 10, 25]"
            showFirstLastButtons
            class="legacy-layout"
            data-cy="tax-payments-paginator"
          ></mat-paginator>
        </div>

        <!--
          Legal disclosure (not co-branding). Renders for both populated and
          empty states once the section is LOADED so the "processed by
          Remitian" attribution is consistently visible whenever the section
          is rendered. Skipped during LOADING / ERROR to avoid attributing a
          surface that hasn't fully resolved.
        -->
        <div class="tax-payments__footer" data-cy="tax-payments-disclosure">
          <span>Tax payments processed by</span>
          <img
            src="assets/remitian-logo.svg"
            alt="Remitian"
            class="tax-payments__remitian-logo"
            data-cy="tax-payments-remitian-logo"
          />
        </div>
      </ng-container>
    </ng-container>
  </ng-container>
</div>
